import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import type { HomeMenuItem } from './home-menu-config';

export interface HomeMenuButtonProps {
  item: HomeMenuItem;
  className?: string;
}

/**
 * Prefer a two-line layout when the item was designed that way and the
 * translated string still has a natural word break.
 */
function displayLines(
  translated: string,
  prefersTwoLines: boolean,
): readonly [string, string] | null {
  if (!prefersTwoLines) return null;
  const parts = translated.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return null;
  return [parts[0]!, parts.slice(1).join(' ')];
}

/**
 * Figma HomeMenuButton — homepage product tile.
 * Fill bound to action/primary; label to text/on-action; art from Assets ME.
 */
export function HomeMenuButton({ item, className }: HomeMenuButtonProps) {
  const { t } = useTranslation();
  const translated = t(item.translateKey, { defaultValue: item.label });
  const lines = displayLines(translated, Boolean(item.labelLines));

  return (
    <Link
      to={item.to}
      aria-label={translated}
      className={cn(
        'relative flex h-[150px] w-[110px] shrink-0 flex-col items-center justify-between',
        'overflow-hidden rounded-[6px] px-2.5 pt-[15px] pb-2.5',
        'transition-opacity hover:opacity-90',
        className,
      )}
      style={{
        background:
          'radial-gradient(105.27% 90.32% at 85.94% 110.15%, #F0E1D7 0%, rgba(90, 32, 228, 0.00) 100%), radial-gradient(269.3% 172.81% at 6.09% 0%, #0B121F 5.92%, rgba(11, 18, 31, 0.00) 100%), var(--color-action-primary)',
      }}
    >
      <span
        className={cn(
          'relative z-10 text-center font-sans text-[18px] font-bold italic',
          'leading-[0.9] tracking-[-0.54px] text-text-on-action',
        )}
      >
        {lines ? (
          <>
            <span className="block">{lines[0]}</span>
            <span className="block">{lines[1]}</span>
          </>
        ) : (
          translated
        )}
      </span>

      <span className="relative z-10 size-[90px] shrink-0 overflow-hidden">
        <img
          src={item.image}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          className={cn('pointer-events-none absolute', item.imageClassName)}
        />
      </span>
    </Link>
  );
}
